import { useState } from 'react'
import Image from 'next/image'
import numeral from 'numeral'

import Container from '/src/components/@common.blocks/Container'
import Title from '/src/components/@common.blocks/Title'
import Text from '/src/components/@common.blocks/Text'
import Avatar from '/src/components/@common.blocks/Avatar'
import Skeleton from '/src/components/@common.blocks/Skeleton'
import { formatNumber, cutAddress } from '/src/helpers/formatter'
import {
  TwitterIcon,
  InstagramIcon,
  OpenseaIcon,
  DiscordIcon,
  WebsiteIcon,
  GemIcon,
  LooksrareIcon,
} from '/src/components/@common.blocks/Icon'

import blockchainIcons from '/src/constants/blockchainIcons'

const PrefaceCollection = ({ data }) => {
  const [imgLoading, setImgLoading] = useState(true)

  return (
    <section className='preface-collection'>
      <div className='preface-collection__cover'>
        <Skeleton loading={imgLoading} active={false}>
          {data.bannerImage && (
            <Image
              src={data.bannerImage}
              alt={data.name}
              layout='fill'
              onLoadingComplete={() => setImgLoading(false)}
            />
          )}
        </Skeleton>
      </div>
      <div className='preface-collection__content'>
        <Container className='preface-collection__container'>
          <div className='preface-collection__logo'>
            <Avatar src={data.image} alt={data.name} size='xl' border='white' />
          </div>
          <Title level={2} className='preface-collection__title'>
            {data.name}
            {data.verified && (
              <img
                src='/icons/bluemark.svg'
                alt='verified'
                className='preface-collection__verified'
              />
            )}
          </Title>
          <Text className='text-center text-break-word' color='light-gray'>
            {data.description}
          </Text>
          <div className='preface-collection__socials'>
            {data.website && (
              <a
                href={data.website}
                rel='noreferrer'
                target='_blank'
                aria-label='Official Website'
              >
                <WebsiteIcon />
              </a>
            )}
            {data.discord && (
              <a
                href={data.discord}
                rel='noreferrer'
                target='_blank'
                aria-label='Discord'
              >
                <DiscordIcon />
              </a>
            )}
            {data.opensea && (
              <a
                href={`https://opensea.io/collection/${data.opensea}`}
                rel='noreferrer'
                target='_blank'
                aria-label='Opensea'
              >
                <OpenseaIcon />
              </a>
            )}
            {data.contract && (
              <>
                <a
                  href={`https://www.gem.xyz/collection/${data.contract}`}
                  rel='noreferrer'
                  target='_blank'
                  aria-label='Gem'
                >
                  <GemIcon />
                </a>
                <a
                  href={`https://looksrare.org/collections/${data.contract}`}
                  rel='noreferrer'
                  target='_blank'
                  aria-label='Looksrare'
                >
                  <LooksrareIcon />
                </a>
              </>
            )}
            {data.twitter && (
              <a
                href={`https://twitter.com/${data.twitter}`}
                rel='noreferrer'
                target='_blank'
                aria-label='Twitter'
              >
                <TwitterIcon />
              </a>
            )}
            {data.instagram && (
              <a
                href={`https://www.instagram.com/${data.instagram}`}
                rel='noreferrer'
                target='_blank'
                aria-label='Instagram'
              >
                <InstagramIcon />
              </a>
            )}
          </div>
          <a
            href={`https://etherscan.io/address/${data.contract}`}
            target='_blank'
            rel='noreferrer'
            className='preface-collection__contract'
          >
            <img src='/ui-icons/etherscan.svg' alt='etherscan' />
            <Text
              size='sm'
              color='light-gray'
              weight='semi-bold'
              component='span'
            >
              {cutAddress(data.contract, 5)}
            </Text>
          </a>
        </Container>
        <Container className='preface-collection__stats'>
          <div className='preface-collection__stats-border' />
          <div className='preface-collection__stats-border' />
          <div className='preface-collection__stats-grid'>
            <div>
              <Text
                color='light-gray'
                weight='medium'
                className='preface-collection__stats-name'
              >
                Blockchain
              </Text>
              <Text className='text-capitalize'>{data.blockchain}</Text>
            </div>
            <div>
              <Text
                color='light-gray'
                weight='medium'
                className='preface-collection__stats-name'
              >
                Total Volume
              </Text>
              <Text className='preface-collection__stats-field'>
                <Image
                  src={blockchainIcons[data.blockchain]}
                  alt='ethereum'
                  height={14}
                  width={14}
                />
                {numeral(data.stats?.totalVolume).format('0.[0]a')}
              </Text>
            </div>
            <div>
              <Text
                color='light-gray'
                weight='medium'
                className='preface-collection__stats-name'
              >
                Floor Price
              </Text>
              <Text className='preface-collection__stats-field'>
                <Image
                  src={blockchainIcons[data.blockchain]}
                  alt='ethereum'
                  height={14}
                  width={14}
                />
                {formatNumber(data.stats?.floorPrice || 0, 2) || 0}
              </Text>
            </div>
            <div>
              <Text
                color='light-gray'
                weight='medium'
                className='preface-collection__stats-name'
              >
                Owners
              </Text>
              <Text className='preface-collection__stats-field'>
                {numeral(data.stats?.owners).format('0.[0]a')}
              </Text>
            </div>
            <div>
              <Text
                color='light-gray'
                weight='medium'
                className='preface-collection__stats-name'
              >
                Items
              </Text>
              <Text className='preface-collection__stats-field'>
                {numeral(data.stats?.totalSupply).format('0.[0]a')}
              </Text>
            </div>
          </div>
        </Container>
      </div>
    </section>
  )
}

export default PrefaceCollection
